/**
 * Broker Account List Page (Web)
 * Displays list of linked broker accounts
 * 
 * ⚠️ IMPORTANT: Per ADR-FE-001, this component only displays broker account data.
 * All broker account operations are handled by backend Broker Integration Service.
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SelectChangeEvent } from '@mui/material';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  LinkOff as DisconnectIcon,
  Link as ReconnectIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useBrokerAccounts } from '@shared/features/broker-accounts';
import { formatCurrency } from '@shared/utils/formatters';
import { AuthenticatedLayout } from '../../components/layout/AuthenticatedLayout';

/** Maps broker_name to local logo path for table display */
function getBrokerLogoUrl(brokerName: string): string | undefined {
  const logoMap: Record<string, string> = {
    zerodha: '/assets/brokers/zerodha-logo.svg',
    angelone: '/assets/brokers/AngelOneLogo.png',
    upstox: '/assets/brokers/upstox-new-logo.svg',
    dhan: '/assets/brokers/Dhan-logo.svg',
    '5paisa': '/assets/brokers/5paisa-logo.svg',
    aliceblue: '/assets/brokers/aliceblue-logo.webp',
    'alice blue': '/assets/brokers/aliceblue-logo.webp',
    'axis securities': '/assets/brokers/Axis-Securites-logo.png',
    axis: '/assets/brokers/Axis-Securites-logo.png',
    edelweiss: '/assets/brokers/Edelweiss-logo.png',
    fyers: '/assets/brokers/fyers-logo.svg',
    groww: '/assets/brokers/groww-logo.svg',
    'hdfc securities': '/assets/brokers/HDFC Securities logo.png',
    hdfc: '/assets/brokers/HDFC Securities logo.png',
    'icici securities': '/assets/brokers/icici-securities-logo.svg',
    icici: '/assets/brokers/icici-securities-logo.svg',
    'iifl capital': '/assets/brokers/iifl-capital-new-logo.png',
    iifl: '/assets/brokers/iifl-capital-new-logo.png',
    'kotak securities': '/assets/brokers/kotak-securities-logo.jpg',
    kotak: '/assets/brokers/kotak-securities-logo.jpg',
    'mirae asset': '/assets/brokers/mirae-asset-sharekhan-logo.png',
    'mirae asset sharekhan': '/assets/brokers/mirae-asset-sharekhan-logo.png',
    sharekhan: '/assets/brokers/mirae-asset-sharekhan-logo.png',
    'motilal oswal': '/assets/brokers/motilal oswal logo.svg',
    'nirmal bang': '/assets/brokers/nirmal bang-logo.png',
    'paytm money': '/assets/brokers/paytm-money-logo.jpg',
    paytm: '/assets/brokers/paytm-money-logo.jpg',
    pml: '/assets/brokers/pml-logo.webp',
    samco: '/assets/brokers/samco-white-logo.svg',
    'sbi securities': '/assets/brokers/sbi-securities-logo.png',
    sbi: '/assets/brokers/sbi-securities-logo.png',
  };
  return logoMap[brokerName?.toLowerCase()];
}

export function BrokerAccountListPage() {
  const navigate = useNavigate();
  const {
    accounts,
    loading,
    error,
    loadAccounts,
    disconnectAccount,
    deregisterAccount,
    reconnectAccount,
  } = useBrokerAccounts();
  const [disconnectDialogOpen, setDisconnectDialogOpen] = React.useState(false);
  const [accountToDisconnect, setAccountToDisconnect] = React.useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [accountToDelete, setAccountToDelete] = React.useState<string | null>(null);
  const [reconnectSuccessDialogOpen, setReconnectSuccessDialogOpen] = React.useState(false);
  const [reconnectedAccountName, setReconnectedAccountName] = React.useState<string | null>(null);
  const [reconnectErrorDialogOpen, setReconnectErrorDialogOpen] = React.useState(false);
  const [reconnectErrorMessage, setReconnectErrorMessage] = React.useState<string | null>(null);
  const [mustDisconnectDialogOpen, setMustDisconnectDialogOpen] = React.useState(false);
  const [reconnectConfirmDialogOpen, setReconnectConfirmDialogOpen] = React.useState(false);
  const [accountToReconnect, setAccountToReconnect] = React.useState<string | null>(null);
  const [accountToReconnectAuthMode, setAccountToReconnectAuthMode] = React.useState<string | null>(null);
  const [accountToReconnectBrokerName, setAccountToReconnectBrokerName] = React.useState<string | null>(null);
  const [searchText, setSearchText] = React.useState('');
  const [filterBroker, setFilterBroker] = React.useState<string>('all');
  const [filterStatus, setFilterStatus] = React.useState<string>('all');
  const [hasLoadedOnce, setHasLoadedOnce] = React.useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadPromise = loadAccounts()
      .then(() => {
        if (isMounted) {
          setHasLoadedOnce(true);
        }
      })
      .catch((error) => {
        console.error('[BrokerAccountListPage] Failed to load accounts:', error);
        if (isMounted) {
          setHasLoadedOnce(true);
        }
      });

    // Add a timeout safeguard - if loading takes more than 35 seconds, 
    // mark as loaded to prevent infinite loading state
    const timeoutId = setTimeout(() => {
      if (isMounted) {
        setHasLoadedOnce(true);
      }
    }, 35000);

    loadPromise.finally(() => {
      clearTimeout(timeoutId);
    });

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [loadAccounts]);

  const handleAddBroker = () => {
    navigate('/broker-accounts/add');
  };

  const handleDisconnectClick = (accountId: string) => {
    setAccountToDisconnect(accountId);
    setDisconnectDialogOpen(true);
  };

  const handleDisconnectConfirm = async () => {
    if (accountToDisconnect) {
      await disconnectAccount(accountToDisconnect);
      setDisconnectDialogOpen(false);
      setAccountToDisconnect(null);
    }
  };

  const handleDisconnectCancel = () => {
    setDisconnectDialogOpen(false);
    setAccountToDisconnect(null);
  };

  const handleReconnectClick = (accountId: string) => {
    const account = accounts.find((acc) => acc.id === accountId);
    if (account) {
      setAccountToReconnect(account.id);
      setAccountToReconnectAuthMode(account.auth_mode || null);
      setAccountToReconnectBrokerName(getBrokerDisplayName(account.broker_name));
      setReconnectConfirmDialogOpen(true);
    }
  };

  const handleReconnectCancel = () => {
    setReconnectConfirmDialogOpen(false);
    setAccountToReconnect(null);
    setAccountToReconnectAuthMode(null);
    setAccountToReconnectBrokerName(null);
  };

  const handleReconnectConfirm = async () => {
    if (!accountToReconnect) return;

    // Close the confirmation dialog
    setReconnectConfirmDialogOpen(false);

    const account = accounts.find((acc) => acc.id === accountToReconnect);
    if (account) {
      try {
        const result = await reconnectAccount(account.id);

        // Check if reconnection was successful
        const isSuccess = result.is_connected ||
          result.status === 'connected' ||
          result.status === 'completed' ||
          result.connection_status === 'connected' ||
          result.connection_status === 'active';

        // Check if reconnection failed
        const isFailed = result.status === 'failed' ||
          (!isSuccess && result.status !== 'oauth_required' && !result.oauth_url);

        // Check auth_mode to determine flow
        if (result.auth_mode === 'totp_automated') {
          // TOTP automated reconnect - show success or error message
          if (isSuccess) {
            // Successfully reconnected automatically
            setReconnectedAccountName(account.account_name || getBrokerDisplayName(account.broker_name));
            setReconnectSuccessDialogOpen(true);
            await loadAccounts(); // Refresh to show updated status
          } else if (isFailed) {
            // Reconnection failed - show error dialog
            const errorMsg = result.message || 'Failed to reconnect account. Please try again.';
            setReconnectErrorMessage(errorMsg);
            setReconnectErrorDialogOpen(true);
            await loadAccounts(); // Refresh to show updated status
          } else {
            // Reconnection in progress or unknown status
            await loadAccounts(); // Refresh to show updated status
          }
        } else if (result.oauth_url) {
          // OAuth flow - redirect to OAuth URL
          window.location.href = result.oauth_url;
        } else {
          // Account is already connected or other status
          if (isSuccess) {
            setReconnectedAccountName(account.account_name || getBrokerDisplayName(account.broker_name));
            setReconnectSuccessDialogOpen(true);
          } else if (isFailed) {
            // Reconnection failed - show error dialog
            const errorMsg = result.message || 'Failed to reconnect account. Please try again.';
            setReconnectErrorMessage(errorMsg);
            setReconnectErrorDialogOpen(true);
          }
          await loadAccounts();
        }
      } catch (error) {
        console.error('Failed to reconnect account:', error);
        // Show error dialog with error message
        const errorMsg = error instanceof Error ? error.message : 'Failed to reconnect account. Please try again.';
        setReconnectErrorMessage(errorMsg);
        setReconnectErrorDialogOpen(true);
      } finally {
        // Reset state after attempting reconnect
        setAccountToReconnect(null);
        setAccountToReconnectAuthMode(null);
        setAccountToReconnectBrokerName(null);
      }
    }
  };

  const handleDeleteClick = (accountId: string) => {
    const account = accounts.find((acc) => acc.id === accountId);
    if (account) {
      // Check if account is connected (active or status is 'connected')
      const isConnected = account.active ||
        account.status?.toLowerCase() === 'connected' ||
        account.status?.toLowerCase() === 'active';

      if (isConnected) {
        // Account is connected, show message that it needs to be disconnected first
        setMustDisconnectDialogOpen(true);
      } else {
        // Account is not connected, proceed with delete dialog
        setAccountToDelete(accountId);
        setDeleteDialogOpen(true);
      }
    }
  };

  const handleDeleteConfirm = async () => {
    if (accountToDelete) {
      try {
        await deregisterAccount(accountToDelete);
        setDeleteDialogOpen(false);
        setAccountToDelete(null);
      } catch (error) {
        console.error('Failed to delete account:', error);
        // Error is already handled by the Redux slice and will be displayed in the error state
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAccountToDelete(null);
  };

  const handleReconnectSuccessClose = () => {
    setReconnectSuccessDialogOpen(false);
    setReconnectedAccountName(null);
  };

  const handleReconnectErrorClose = () => {
    setReconnectErrorDialogOpen(false);
    setReconnectErrorMessage(null);
  };

  const handleMustDisconnectClose = () => {
    setMustDisconnectDialogOpen(false);
  };

  const getBrokerDisplayName = (brokerName: string) => {
    const names: Record<string, string> = {
      zerodha: 'Zerodha',
      angelone: 'Angel One',
      upstox: 'Upstox',
      dhan: 'Dhan',
    };
    return names[brokerName] || brokerName;
  };

  const formatSessionStatus = (status?: string | null) => {
    if (!status) {
      return '—';
    }
    const normalized = status.replace(/_/g, ' ').trim().toLowerCase();
    if (!normalized) {
      return '—';
    }
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  const getSessionColor = (status?: string | null) => {
    if (!status) {
      return 'text.primary';
    }
    const normalized = status.trim().toLowerCase();
    return normalized === 'connected' ? 'success.main' : 'error.main';
  };

  // Filter accounts based on search and filters
  const filteredAccounts = React.useMemo(() => {
    return accounts.filter((account) => {
      // Search filter - search in broker name and account name
      const searchLower = searchText.toLowerCase();
      const brokerDisplayName = getBrokerDisplayName(account.broker_name).toLowerCase();
      const accountName = (account.account_name || '').toLowerCase();
      const matchesSearch =
        !searchText ||
        brokerDisplayName.includes(searchLower) ||
        accountName.includes(searchLower);

      // Broker filter
      const matchesBroker = filterBroker === 'all' || account.broker_name === filterBroker;

      // Status filter
      const matchesStatus = filterStatus === 'all' ||
        (filterStatus === 'active' && account.active) ||
        (filterStatus === 'inactive' && !account.active);

      return matchesSearch && matchesBroker && matchesStatus;
    });
  }, [accounts, searchText, filterBroker, filterStatus]);

  // Get unique brokers for filter dropdown
  const uniqueBrokers = React.useMemo(() => {
    const brokers = new Set(accounts.map((acc) => acc.broker_name));
    return Array.from(brokers);
  }, [accounts]);

  const handleClearSearch = () => {
    setSearchText('');
  };

  const handleClearFilters = () => {
    setSearchText('');
    setFilterBroker('all');
    setFilterStatus('all');
  };

  // Show loading spinner only if we're loading AND haven't loaded at least once
  // This prevents infinite loading when there are no accounts
  if (loading && accounts.length === 0 && !hasLoadedOnce) {
    return (
      <AuthenticatedLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </AuthenticatedLayout>
    );
  }

  return (
    <AuthenticatedLayout>
      <Box>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {accounts.length > 0 && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center" justifyContent="space-between" flexWrap="wrap">
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  placeholder="Search by broker or account name..."
                  value={searchText}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value)}
                  size="small"
                  sx={{
                    flexGrow: 1,
                    maxWidth: { xs: '100%', sm: 400 },
                    '& .MuiInputBase-root': {
                      height: '32px',
                      fontSize: '0.75rem',
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.75rem',
                      py: '4px',
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchText && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={handleClearSearch}
                          edge="end"
                          sx={{ p: 0.5 }}
                        >
                          <ClearIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel sx={{ fontSize: '0.75rem' }}>Broker</InputLabel>
                  <Select
                    value={filterBroker}
                    label="Broker"
                    onChange={(e: SelectChangeEvent<string>) => setFilterBroker(e.target.value)}
                    sx={{
                      height: '32px',
                      fontSize: '0.75rem',
                      '& .MuiSelect-select': {
                        fontSize: '0.75rem',
                        py: '4px',
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          '& .MuiMenuItem-root': {
                            fontSize: '0.75rem',
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem value="all">All Brokers</MenuItem>
                    {uniqueBrokers.map((broker) => (
                      <MenuItem key={broker} value={broker}>
                        {getBrokerDisplayName(broker)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel sx={{ fontSize: '0.75rem' }}>Status</InputLabel>
                  <Select
                    value={filterStatus}
                    label="Status"
                    onChange={(e: SelectChangeEvent<string>) => setFilterStatus(e.target.value)}
                    sx={{
                      height: '32px',
                      fontSize: '0.75rem',
                      '& .MuiSelect-select': {
                        fontSize: '0.75rem',
                        py: '4px',
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          '& .MuiMenuItem-root': {
                            fontSize: '0.75rem',
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <Tooltip title="Refresh">
                  <IconButton
                    onClick={loadAccounts}
                    disabled={loading}
                    color="primary"
                    size="small"
                    sx={{
                      border: '1px solid',
                      borderColor: 'primary.main',
                      borderRadius: '50%',
                      width: 32,
                      height: 32,
                    }}
                  >
                    <RefreshIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Tooltip>
                {(searchText || filterBroker !== 'all' || filterStatus !== 'all') && (
                  <Button
                    size="small"
                    onClick={handleClearFilters}
                    startIcon={<ClearIcon sx={{ fontSize: '0.875rem' }} />}
                    sx={{
                      whiteSpace: 'nowrap',
                      fontSize: '0.75rem',
                      height: '32px',
                      px: 1.5,
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Stack>
          </Paper>
        )}

        {accounts.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No broker accounts linked
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Link your broker accounts to start trading across multiple brokers
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddBroker}>
                Add Your First Broker Account
              </Button>
            </CardContent>
          </Card>
        ) : filteredAccounts.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No accounts match your filters
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Try adjusting your search or filter criteria
              </Typography>
              <Button variant="outlined" onClick={handleClearFilters} startIcon={<ClearIcon />}>
                Clear All Filters
              </Button>
            </CardContent>
          </Card>
        ) : (
          <TableContainer component={Paper} elevation={0} sx={{ border: 'none' }}>
            <Table
              sx={{
                minWidth: 650,
                '& .MuiTableCell-root': {
                  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                },
              }}
              aria-label="broker accounts table"
              size="small"
            >
              <TableHead>
                <TableRow sx={{ bgcolor: 'rgba(4, 55, 123, 0.05)' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.75rem', py: 1 }}>Account ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.75rem', py: 1 }}>Account Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.75rem', py: 1 }}>Broker</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.75rem', py: 1 }}>Balance</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.75rem', py: 1 }}>Session</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.75rem', py: 1 }}>Linked Date</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.75rem', py: 1 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAccounts.map((account) => (
                  <TableRow
                    key={account.id}
                    sx={{
                      '&:nth-of-type(odd)': { bgcolor: 'rgba(4, 55, 123, 0.015)' },
                      '&:hover': { bgcolor: 'rgba(4, 55, 123, 0.02)' },
                      '&:last-of-type td': { borderBottom: '1px solid rgba(0, 0, 0, 0.08)' },
                    }}
                  >
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                        {account.broker_account_id || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                        {account.account_name || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                        {getBrokerLogoUrl(account.broker_name) ? (
                          <Tooltip title={getBrokerDisplayName(account.broker_name)} enterDelay={300}>
                            <Box
                              component="img"
                              src={getBrokerLogoUrl(account.broker_name)}
                              alt={getBrokerDisplayName(account.broker_name)}
                              sx={{
                                width: 88,
                                height: 44,
                                objectFit: 'contain',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'background.paper',
                                p: 1,
                                boxSizing: 'border-box',
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" sx={{ fontSize: '0.8125rem', textTransform: 'capitalize' }}>
                            {getBrokerDisplayName(account.broker_name)}
                          </Typography>
                        )}
                        {account.auth_mode === 'totp_automated' && (
                          <Chip
                            label="Automated"
                            size="small"
                            color="success"
                            sx={{
                              height: '18px',
                              fontSize: '0.625rem',
                              '& .MuiChip-label': { px: 0.75 }
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                        {typeof account.balance === 'number' ? formatCurrency(account.balance) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: '0.8125rem', color: getSessionColor(account.status) }}
                      >
                        {formatSessionStatus(account.status)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                        {new Date(account.linked_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1 }}>
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        {!account.active && (
                          <Tooltip title="Reconnect account">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleReconnectClick(account.id)}
                              aria-label="Reconnect account"
                              sx={{
                                '&:hover': {
                                  bgcolor: 'primary.light',
                                  color: 'primary.contrastText',
                                },
                              }}
                            >
                              <ReconnectIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {account.active && (
                          <Tooltip title="Disconnect account">
                            <IconButton
                              color="warning"
                              size="small"
                              onClick={() => handleDisconnectClick(account.id)}
                              aria-label="Disconnect account"
                              sx={{
                                '&:hover': {
                                  bgcolor: 'warning.light',
                                  color: 'warning.contrastText',
                                },
                              }}
                            >
                              <DisconnectIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete account">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleDeleteClick(account.id)}
                            aria-label="Delete account"
                            sx={{
                              '&:hover': {
                                bgcolor: 'error.light',
                                color: 'error.contrastText',
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Disconnect Confirmation Dialog */}
        <Dialog open={disconnectDialogOpen} onClose={handleDisconnectCancel}>
          <DialogTitle>Disconnect Broker Account?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to disconnect this broker account? You will need to link it again to use it for trading.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDisconnectCancel}>Cancel</Button>
            <Button onClick={handleDisconnectConfirm} color="warning" variant="contained">
              Disconnect
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
          <DialogTitle>Delete Broker Account?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to permanently delete this broker account? This action cannot be undone and all associated data will be removed.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reconnect Success Dialog */}
        <Dialog
          open={reconnectSuccessDialogOpen}
          onClose={handleReconnectSuccessClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
            <CheckCircleIcon color="success" />
            Account Connected Successfully
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {reconnectedAccountName
                ? `Your broker account "${reconnectedAccountName}" has been successfully reconnected and is now active.`
                : 'Your broker account has been successfully reconnected and is now active.'}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleReconnectSuccessClose} color="primary" variant="contained">
              OK
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reconnect Error Dialog */}
        <Dialog
          open={reconnectErrorDialogOpen}
          onClose={handleReconnectErrorClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
            <ErrorIcon color="error" />
            Reconnection Failed
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {reconnectErrorMessage || 'Failed to reconnect your broker account. Please try again or contact support if the issue persists.'}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleReconnectErrorClose} color="primary" variant="contained">
              OK
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reconnect Confirmation Dialog */}
        <Dialog open={reconnectConfirmDialogOpen} onClose={handleReconnectCancel} maxWidth="sm" fullWidth>
          <DialogTitle>Reconnect Broker Account</DialogTitle>
          <DialogContent sx={{ pb: 1 }}>
            {accountToReconnectAuthMode === 'totp_automated' ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Automated Reconnection
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  This account uses automated TOTP and will be reconnected securely in the background.
                </Typography>
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  OAuth Redirect Required
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  You will be redirected to <strong>{accountToReconnectBrokerName}</strong> to securely authorise the connection.
                </Typography>
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleReconnectCancel}>Cancel</Button>
            <Button onClick={handleReconnectConfirm} color="primary" variant="contained">
              Proceed to Reconnect
            </Button>
          </DialogActions>
        </Dialog>

        {/* Must Disconnect Before Delete Dialog */}
        <Dialog
          open={mustDisconnectDialogOpen}
          onClose={handleMustDisconnectClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}>
            <ErrorIcon color="warning" />
            Account Must Be Disconnected
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              This account is currently connected. Please disconnect the account before deleting it.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleMustDisconnectClose} color="primary" variant="contained">
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AuthenticatedLayout>
  );
}

